/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { Either, Option, ReadonlyArray } from "@morphism/fp";
import React from "react";
import styled from "styled-components";

import { Asset } from "~/Asset";
import { DVIR, DVIRs } from "~/DVIR";
import { ID } from "~/ID";
import * as Layout from "~/Layout";
import { Mutable } from "~/Mutable";
import { NotApplicable } from "~/NotApplicable";
import { Order } from "~/Order";
import { Select as GenericSelect } from "~/Select";

const isEmptyVehicle = (vehicle: Asset.OptionalResult): boolean =>
  Either.getOrElseW(() => undefined)(vehicle) === undefined;

export function Assets({
  type,
  dvir,
}: {
  readonly type: Assets.Type;
  readonly dvir: DVIR;
}) {
  return pipe(
    type === "vehicle"
      ? isEmptyVehicle(dvir.vehicle)
        ? []
        : [dvir.vehicle as Asset.Result]
      : dvir.trailers,
    ReadonlyArray.rights,
    ReadonlyArray.sort(Asset.order()),
    ReadonlyArray.map(({ asset }) => (
      <Assets.Row key={asset.id}>
        <Asset.Link noLink asset={asset} />
      </Assets.Row>
    )),
    ([first, ...rest]) => (
      <>{first ? [first, ...rest] : <NotApplicable>None</NotApplicable>}</>
    )
  );
}

export namespace Assets {
  export type Type = "vehicle" | "trailers";

  export const column = (type: "vehicle" | "trailers") => ({
    dvirs,
    assets,
    onChange,
  }: {
    // eslint-disable-next-line functional/prefer-readonly-type
    readonly dvirs: DVIR[];
    readonly assets?: readonly ID[];
    readonly onChange: (assets?: readonly ID[]) => void;
  }) => ({
    title: type === "vehicle" ? "Vehicle" : "Trailers",
    dataIndex: type,
    filteredValue: assets && [type],
    filterDropdown: (props: { readonly confirm: () => void }) => (
      <Filter {...{ type, dvirs, assets, onChange }} {...props} />
    ),
    onFilter: (_: unknown, dvir: DVIR) =>
      pipe(dvir, getFilterFunc(type, assets)),
    render: (_: unknown, dvir: DVIR) => <Assets type={type} dvir={dvir} />,
    sorter: (a: DVIR, b: DVIR): Order.Ordering =>
      type === "vehicle"
        ? pipe(
            [a.vehicle, b.vehicle],
            ReadonlyArray.map(Either.getOrElseW(() => undefined)),
            ([a, b]) => Order.optionally(Asset.compare)(a, b)
          )
        : pipe(
            [a.trailers, b.trailers],
            ReadonlyArray.map(ReadonlyArray.rights),
            ([a, b]) =>
              pipe(Order.number().compare(a.length, b.length), (lengths) =>
                lengths !== Order.equal()
                  ? lengths
                  : pipe(
                      [a, b],
                      ReadonlyArray.map(ReadonlyArray.sort(Asset.order())),
                      ([[a], [b]]) => Order.optionally(Asset.compare)(a, b)
                    )
              )
          ),
  });

  export const Row = styled.div`
    margin-bottom: ${Layout.Block.padding()};
    &:last-of-type {
      margin-bottom: 0;
    }
  `;

  export const getFilterFunc = (type: Type, assets: readonly ID[] = []) => (
    dvir: DVIR
  ) => {
    const ids = assets.map(({ id }) => id);
    return !ids[0]
      ? true
      : pipe(
          type === "vehicle"
            ? isEmptyVehicle(dvir.vehicle)
              ? []
              : [dvir.vehicle as Asset.Result]
            : dvir.trailers,
          ReadonlyArray.rights,
          ReadonlyArray.findFirst(({ asset }) => ids.includes(asset.id)),
          Option.isSome
        );
  };

  export const getFilterFuncForSettings = (
    type: Type,
    settings: DVIRs.Table.Settings
  ) =>
    type === "vehicle"
      ? getFilterFunc(type, settings.vehicles)
      : getFilterFunc(type, settings.trailers);

  export const Filter = ({
    type,
    dvirs,
    loading,
    assets,
    onChange,
    confirm,
  }: DVIRs.Table.Filter.Props &
    Parameters<ReturnType<typeof column>>[0] & { readonly type: Type }) => {
    const selected = (assets ?? []).map(({ id }) => id);
    const options = pipe(
      dvirs,
      ReadonlyArray.filterMap((dvir) =>
        type == "vehicle"
          ? isEmptyVehicle(dvir.vehicle)
            ? Option.none
            : Option.some([dvir.vehicle as Asset.Result])
          : dvir.trailers.length > 0
          ? Option.some(dvir.trailers)
          : Option.none
      ),
      ReadonlyArray.reduce<readonly Asset.Result[], readonly Asset.Result[]>(
        [],
        (reduced, assets) => [...reduced, ...assets]
      ),
      ReadonlyArray.rights,
      ReadonlyArray.sort(Asset.order()),
      ReadonlyArray.map(({ asset }) => ({
        key: asset.id,
        value: Asset.name({ asset }),
      })),
      ReadonlyArray.uniq({ equals: ({ key: a }, { key: b }) => a === b })
    );

    const value = pipe(
      options,
      ReadonlyArray.filterMap(({ key, value }) =>
        selected.includes(key) ? Option.some(value) : Option.none
      )
    );

    return (
      <DVIRs.Table.Filter confirm={confirm} onReset={onChange}>
        <GenericSelect
          loading={loading}
          style={{ width: 200 }}
          dropdownMatchSelectWidth
          placeholder={`Filter by ${type}`}
          mode="multiple"
          bordered={false}
          options={options as Mutable<typeof options>}
          value={value as Mutable<typeof value>}
          onChange={
            ((_value: never, selected: readonly { readonly key: string }[]) =>
              onChange(selected.map(({ key: id }) => ({ id })))) as never
          }
        />
      </DVIRs.Table.Filter>
    );
  };
}
